/**
 * KHIYARSA (KMD): "AI Diagram Template" dialog — the public hand-out flow.
 * Explains the external-AI workflow (template → agent → USER REQUEST → .md →
 * Import Markdown) and offers View / Copy / Download of the canonical
 * template plus a copyable example request. Content is always fetched from
 * TEMPLATE_PUBLIC_PATH (nginx alias of the committed file) — never bundled.
 */
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useApp } from "@excalidraw/excalidraw/components/App";
import { useCallback, useEffect, useState } from "react";

import { useAtom } from "../../../app-jotai";
import { useAppLangCode } from "../../../app-language/language-state";

import {
  EXAMPLE_REQUEST_AR,
  EXAMPLE_REQUEST_EN,
  TEMPLATE_FILENAME,
  TEMPLATE_META,
  TEMPLATE_PUBLIC_PATH,
  fetchTemplate,
} from "./agentTemplate";
import { agentTemplateDialogAtom } from "./atoms";
import { useKmdT } from "./strings";

import "./AgentTemplateDialog.scss";

export const KmdAgentTemplateDialog = () => {
  const t = useKmdT();
  const app = useApp();
  const [langCode] = useAppLangCode();
  const [dialog, setDialog] = useAtom(agentTemplateDialogAtom);
  const [template, setTemplate] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [viewing, setViewing] = useState(false);

  const toast = (message: string) =>
    (app as unknown as { setToast: (t: { message: string }) => void }).setToast(
      { message },
    );

  useEffect(() => {
    if (!dialog.open) {
      setViewing(false);
      return;
    }
    setError(false);
    fetchTemplate().then(setTemplate, () => setError(true));
  }, [dialog.open]);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(await fetchTemplate());
      toast(t("copied"));
    } catch {
      setError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onDownload = useCallback(async () => {
    try {
      const text = await fetchTemplate();
      const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = TEMPLATE_FILENAME;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(true);
    }
  }, []);

  const onCopyExample = useCallback(async () => {
    const example = langCode.startsWith("ar")
      ? EXAMPLE_REQUEST_AR
      : EXAMPLE_REQUEST_EN;
    await navigator.clipboard.writeText(example);
    toast(t("copied"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCode, t]);

  if (!dialog.open) {
    return null;
  }

  return (
    <Dialog
      className="kmd-agent-template-dialog"
      size={720}
      onCloseRequest={() => setDialog({ open: false })}
      title={t("atDialogTitle")}
    >
      <div className="kmd-agent-template">
        <p className="kmd-agent-template__intro">{t("atIntro")}</p>

        <h3>{t("atStepsTitle")}</h3>
        <ol className="kmd-agent-template__steps">
          <li>{t("atStep1")}</li>
          <li>{t("atStep2")}</li>
          <li>{t("atStep3")}</li>
          <li>{t("atStep4")}</li>
          <li>{t("atStep5")}</li>
        </ol>

        <div className="kmd-agent-template__actions">
          <button className="kmd-agent-template__primary" onClick={onDownload}>
            {t("atDownload")}
          </button>
          <button onClick={onCopy}>{t("atCopy")}</button>
          <button onClick={() => setViewing((v) => !v)}>
            {viewing ? t("atHide") : t("atView")}
          </button>
          <button onClick={onCopyExample}>{t("atCopyExample")}</button>
          <a
            className="kmd-agent-template__raw"
            href={TEMPLATE_PUBLIC_PATH}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("atRawLink")}
          </a>
        </div>

        {error && (
          <p className="kmd-agent-template__error">{t("atFetchError")}</p>
        )}

        {viewing && (
          <textarea
            className="kmd-agent-template__source"
            readOnly
            dir="ltr"
            value={template ?? t("atLoading")}
          />
        )}

        <h3>{t("atMetaTitle")}</h3>
        <dl className="kmd-agent-template__meta">
          <dt>{t("atMetaSpec")}</dt>
          <dd>{TEMPLATE_META.specVersion}</dd>
          <dt>{t("atMetaFamilies")}</dt>
          <dd>{TEMPLATE_META.families.join(" · ")}</dd>
          <dt>{t("atMetaCompat")}</dt>
          <dd>
            {TEMPLATE_META.compatibleWith.join(", ")} —{" "}
            {t("atMetaCompatOther")}
          </dd>
          <dt>{t("atMetaOutput")}</dt>
          <dd>{t("atMetaOutputValue")}</dd>
          <dt>{t("atMetaImport")}</dt>
          <dd>{t("atMetaImportValue")}</dd>
        </dl>
      </div>
    </Dialog>
  );
};
